import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Plantillas CSV» › the list of global templates with the exchange catalogue, and the edition of one template. The
 * catalogue comes WHOLE (the old route stopped at 100) and with the same shape in both screens; the list carries the
 * seven fields it paints, the edition the document the editor edits and sends back.
 */

/** The modes of `ImportMappingMode` (`import-mappings.repository.ts`). */
export const IMPORT_MAPPING_MODES = ['simple', 'multi'] as const

export const adminImportMappingRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  exchange: z.string().nullable(),
  isActive: z.boolean(),
  isCustomerSelectable: z.boolean(),
  /** Has a published version; a template without one cannot be activated (409 SOURCE_NOT_PUBLISHED). */
  isPublished: z.boolean(),
  updatedAt: z.string(),
})

/** An option of the exchange picker; `supportsCsvImport` decides whether a template may point at it. */
export const adminExchangeOptionSchema = z.strictObject({
  slug: z.string(),
  name: z.string(),
  supportsCsvImport: z.boolean(),
})

const ruleDocument = (what: string) =>
  jsonDocument({
    reason: `a ${what} of the template, edited as a document by the editor and sent back whole`,
  })

/** The document the editor edits and sends back: strict on top; its rules and configurations travel as they are. */
export const adminImportMappingDocumentSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  exchange: z.string().nullable(),
  isGlobal: z.boolean(),
  isActive: z.boolean(),
  isCustomerSelectable: z.boolean(),
  csvConfig: ruleDocument('CSV configuration'),
  columnMapping: ruleDocument('column mapping'),
  additionalInfo: ruleDocument('additional information block'),
  parameterRules: z.array(ruleDocument('parameter rule')),
  complexRules: z.array(ruleDocument('grouping rule')),
  transferRules: z.array(ruleDocument('transfer rule')),
  transformRules: z.array(ruleDocument('symbol replacement')),
  displayName: z.string().nullable(),
  iconUrl: z.string().nullable(),
  instructions: z.string().nullable(),
  videoUrl: z.string().nullable(),
  displayNameTranslations: z.record(z.string(), z.string()),
  instructionsTranslations: z.record(z.string(), z.string()),
  mode: z.enum(IMPORT_MAPPING_MODES),
  configurations: z.array(ruleDocument('per-CSV-type configuration')),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const adminImportMappingUsageSchema = z.strictObject({
  importJobCount: z.number(),
  affectedTenantCount: z.number(),
  importedRowCount: z.number(),
  versionCount: z.number(),
  linkedTicketCount: z.number(),
  isInUse: z.boolean(),
})

export const adminActiveLanguageSchema = z.strictObject({
  code: z.string(),
  label: z.string(),
  isDefault: z.boolean(),
})

export const adminImportMappingsScreen = defineScreen({
  id: 'admin.importMappings',
  path: '/api/screens/admin/import-mappings',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    mappings: screenSection(z.array(adminImportMappingRowSchema)),
    exchangeOptions: screenSection(z.array(adminExchangeOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.importMappings', 'admin.exchanges'],
  budget: { requests: 1, statements: 6, bytes: 120_000, p95Ms: 300 },
  summary: 'CSV templates: every global template and the whole active exchange catalogue',
})

export const adminImportMappingEditScreen = defineScreen({
  id: 'admin.importMappings.edit',
  path: '/api/screens/admin/import-mappings/edit',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid() }),
  response: z.strictObject({
    /** The subject: an unknown id is the 404 of the old route for the WHOLE screen. */
    mapping: adminImportMappingDocumentSchema,
    usage: screenSection(adminImportMappingUsageSchema),
    exchangeOptions: screenSection(z.array(adminExchangeOptionSchema)),
    languages: screenSection(z.array(adminActiveLanguageSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.importMappings', 'admin.exchanges'],
  budget: { requests: 1, statements: 12, bytes: 200_000, p95Ms: 400 },
  summary:
    'One CSV template as the editor edits it, what uses it, the exchange catalogue and the active languages',
})

export type AdminImportMappingRow = z.infer<typeof adminImportMappingRowSchema>
export type AdminExchangeOption = z.infer<typeof adminExchangeOptionSchema>
export type AdminImportMappingDocument = z.infer<typeof adminImportMappingDocumentSchema>
export type AdminImportMappingUsage = z.infer<typeof adminImportMappingUsageSchema>
export type AdminActiveLanguage = z.infer<typeof adminActiveLanguageSchema>
export type AdminImportMappingsData = ScreenData<typeof adminImportMappingsScreen>
export type AdminImportMappingEditData = ScreenData<typeof adminImportMappingEditScreen>
export type AdminImportMappingEditQuery = ScreenQuery<typeof adminImportMappingEditScreen>
